/**
 * Zugriff auf Tabellen, die auf einer Folie liegen.
 *
 * Dass eine Form eine Tabelle ist, verrät schon `Shape.type` ab PowerPointApi
 * 1.4. An den Inhalt kommt man erst ab 1.8 über `Shape.getTable()`. Fehlt der
 * Anforderungssatz, liefert `readTables` `null` – dann lässt sich zwar sagen,
 * dass eine Tabelle da ist, aber nicht, was drinsteht.
 */

import { powerPointApi, supportsPowerPointApi } from "./capabilities";
import { isPowerPointAvailable, PowerPointUnavailableError } from "./presentation";

export interface TableInfo {
  /** Nullbasierte Nummer der Folie, auf der die Tabelle liegt. */
  slideIndex: number;
  shapeId: string;
  rowCount: number;
  columnCount: number;
  /** Zellinhalte, zeilenweise. */
  values: string[][];
}

/**
 * Alle Tabellen der Präsentation mit ihrem Inhalt.
 * `null`, wenn PowerPointApi 1.8 fehlt.
 */
export async function readTables(): Promise<TableInfo[] | null> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();
  if (!supportsPowerPointApi(powerPointApi.tables)) return null;

  try {
    return await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();

      const shapeCollections = slides.items.map((slide) => {
        const shapes = slide.shapes;
        shapes.load("items/id,items/type");
        return shapes;
      });
      await context.sync();

      const requests: Array<{ slideIndex: number; shapeId: string; table: PowerPoint.Table }> = [];
      shapeCollections.forEach((shapes, slideIndex) => {
        for (const shape of shapes.items) {
          if (shape.type !== "Table") continue;
          const table = shape.getTable();
          table.load("rowCount,columnCount,values");
          requests.push({
            slideIndex,
            shapeId: typeof shape.id === "string" ? shape.id : "",
            table,
          });
        }
      });
      if (requests.length === 0) return [];
      await context.sync();

      return requests.map((request) => ({
        slideIndex: request.slideIndex,
        shapeId: request.shapeId,
        rowCount: typeof request.table.rowCount === "number" ? request.table.rowCount : 0,
        columnCount: typeof request.table.columnCount === "number" ? request.table.columnCount : 0,
        values: Array.isArray(request.table.values) ? request.table.values : [],
      }));
    });
  } catch {
    return null;
  }
}

/** Zellen der ersten Zeile – in einer Datentabelle die Spaltenüberschriften. */
export function headerRow(table: TableInfo): string[] {
  return (table.values[0] ?? []).map((cell) => cell.trim());
}

/** Ist die erste Zeile durchgehend gefüllt und damit als Kopfzeile brauchbar? */
export function hasFilledHeaderRow(table: TableInfo): boolean {
  const header = headerRow(table);
  return header.length === table.columnCount && header.every((cell) => cell.length > 0);
}

/** Zellen, in denen mehr als eine Handvoll Wörter steht. */
export function verboseCells(table: TableInfo, maxWords = 8): string[] {
  return table.values
    .flat()
    .map((cell) => cell.trim())
    .filter((cell) => cell.split(/\s+/).filter((word) => word.length > 0).length > maxWords);
}
